import React, { useState } from "react";
import { Shield, Sparkles, ArrowRight, AlertTriangle, AlertCircle, RefreshCw } from "lucide-react";

export default function PlatformPage({ title, desc, tag, pageKey }) {
  const [vendorCode, setVendorCode] = useState("VEND-2026-904");
  const [loading, setLoading] = useState(false);
  const [scoreData, setScoreData] = useState(null);

  // Dynamic illustrative simulation using the provided Emergent API / mock logic
  const handleSimulateScore = async () => {
    setLoading(true);
    setScoreData(null);
    setTimeout(() => {
      let mockScore = {
        score: 87,
        status: "Healthy",
        badgeColor: "bg-green-500/20 text-green-400 border-green-500/30",
        financial: 92,
        delivery: 84,
        compliance: 90,
        incidents: "None recorded last 180 days"
      };

      if (pageKey === "risk") {
        mockScore = {
          score: 54,
          status: "Watchlist Risk",
          badgeColor: "bg-orange-500/20 text-orange-400 border-orange-500/30",
          financial: 61,
          delivery: 52,
          compliance: 49,
          incidents: "Delayed SOC2 audit file upload"
        };
      } else if (pageKey === "spend") {
        mockScore = {
          score: 31,
          status: "High Maveric spend identified",
          badgeColor: "bg-red-500/20 text-red-400 border-red-500/30",
          financial: 88,
          delivery: 91,
          compliance: 95,
          incidents: "Overlap detected: 4 software subscriptions"
        };
      }

      setScoreData(mockScore);
      setLoading(false);
    }, 800);
  };

  return (
    <div className="bg-[#05050A] text-white min-h-screen py-24 relative overflow-hidden" data-testid={`platform-page-${pageKey}`}>
      
      {/* Background soft glow */}
      <div className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-[#7C3AED]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Breadcrumb / Tag */}
        <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-[#EA7C3D] tracking-wide mb-6">
          <Sparkles className="w-3 h-3 text-[#7C3AED]" />
          <span>Platform Capabilities &gt; {tag}</span>
        </div>

        {/* Hero Section */}
        <div className="space-y-6 mb-16">
          <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tighter leading-none">{title}</h1>
          <p className="text-lg text-[#ABA3C2] font-body max-w-3xl leading-relaxed">{desc}</p>
        </div>

        {/* Dynamic Concept Interactive Mockup Widget */}
        <div className="p-8 rounded-3xl bg-[#14101F]/80 border border-white/10 backdrop-blur-xl space-y-6 mb-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#7C3AED]/10 to-transparent pointer-events-none rounded-bl-full" />
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-6">
            <div>
              <h3 className="text-lg font-bold text-white font-display">Aroveniq Sandbox Capability Simulator</h3>
              <p className="text-xs text-zinc-400">See how our background monitoring aggregates signals under this category.</p>
            </div>
            
            {/* Simulation Trigger Button */}
            <button
              onClick={handleSimulateScore}
              disabled={loading}
              data-testid="simulation-trigger"
              className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EA7C3D] text-white text-xs font-semibold hover:shadow-[0_0_15px_rgba(124,58,237,0.4)] transition-all flex items-center space-x-2 self-start sm:self-auto"
            >
              {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
              <span>{loading ? "Parsing Signals..." : "Simulate Scorecard"}</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Simulator Inputs/Fields */}
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#6E6785] uppercase tracking-wider">Target Vendor Code</label>
                <input
                  type="text"
                  value={vendorCode}
                  onChange={(e) => setVendorCode(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#7C3AED] transition-colors"
                />
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Clicking simulate prompts the mock background database to pull compliance filings, public financial audits, and SLA deliveries for this code.
              </p>
            </div>

            {/* Simulated Live Response Display Panel */}
            <div className="p-6 rounded-2xl bg-black/40 border border-white/5 min-h-[160px] flex flex-col justify-center">
              {scoreData ? (
                <div className="space-y-4" data-testid="simulated-result-panel">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs text-zinc-500 font-mono">CODE: {vendorCode}</span>
                      <h4 className="text-sm font-bold text-white mt-1">Vendor Score Matrix</h4>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${scoreData.badgeColor}`}>
                      {scoreData.status}
                    </span>
                  </div>

                  {/* Ratings breakdown */}
                  <div className="grid grid-cols-3 gap-3 pt-2">
                    <div className="p-2.5 rounded-lg bg-white/5 text-center">
                      <div className="text-[10px] text-zinc-500">Financial</div>
                      <div className="text-base font-bold text-white font-mono mt-0.5">{scoreData.financial}/100</div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-white/5 text-center">
                      <div className="text-[10px] text-zinc-500">Delivery</div>
                      <div className="text-base font-bold text-white font-mono mt-0.5">{scoreData.delivery}/100</div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-white/5 text-center">
                      <div className="text-[10px] text-zinc-500">Compliance</div>
                      <div className="text-base font-bold text-white font-mono mt-0.5">{scoreData.compliance}/100</div>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg bg-white/5 border border-white/5 flex items-center space-x-2">
                    <AlertTriangle className="w-4 h-4 text-orange-400 shrink-0" />
                    <span className="text-xs text-zinc-300 font-medium">Trigger Alert: {scoreData.incidents}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-2 text-zinc-500">
                  <AlertCircle className="w-8 h-8 mx-auto text-[#6E6785]" />
                  <p className="text-xs font-semibold">Simulator Ready</p>
                  <p className="text-[10px]">Select simulate above to query continuous platform score card.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Benefits Section block */}
        <div className="space-y-12">
          <h2 className="text-2xl font-bold font-display tracking-tight border-b border-white/10 pb-4">Standard Capability Matrix & Safeguards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-white">Continuous Live Monitoring</h3>
              <p className="text-sm text-[#ABA3C2] leading-relaxed">
                Rather than quarterly audits, Aroveniq parses daily transactional documents, legal registries, and public files to detect issues instantly.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-white">Interoperable ERP Middleware</h3>
              <p className="text-sm text-[#ABA3C2] leading-relaxed">
                Wire seamless event listeners directly to SAP, NetSuite, Coupa, or Zoho with our ready-built webhook system and zero breaking dependencies.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-white">Regulatory Audit Trail</h3>
              <p className="text-sm text-[#ABA3C2] leading-relaxed">
                Maintain immutable SOC2 and ISO compliance documents under a secure decentralized pane, complete with automatic certificate expiration alerts.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-white">Negotiation Leverage</h3>
              <p className="text-sm text-[#ABA3C2] leading-relaxed">
                Utilize spend overlaps and SLA tracking data to negotiate premium discounts on recurring software, raw material, and shipping resources.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}